import {Component, OnInit, ViewChild} from '@angular/core';
import {NavigationItem} from 'features/core/shared/dto/navigation/navigation-item';
import {ObjectBaseModeComponent} from 'features/core/shared/object/mvs-object-base/object-base-mode.component';
import {ProvisionAgentStatementDto} from 'features/feature-prov/cp/model/dto/entity/provision-agent-statement.dto';
import {CpRefreshableStatementList} from 'features/feature-prov/cp/interface/cp-refreshable-statement-list.interface';

/**
 * Base-mode component of the ProvisionAgent OBC (layer 2 of the three-layer pattern, PAT §L.2).
 *
 * Exact repo original: `features/feature-bm/bm/component/object-components/
 * bm-billing-area-object-component/view/bm-billing-area-base.component.ts` — there as here a
 * component WITHOUT its own template (`template: ''`) that declares only the `navigationItems`.
 * `full` and `side` inherit from it and render the matching shared component per navigation item.
 * Because the class is never rendered itself, it deliberately has NO .html/.scss (shape of the
 * guiding precedent, see bm).
 *
 * The navigation is the navItems of 04-frontend.md §7, led by "Kennzahlen" — the KPI pane is the
 * DEFAULT (business rule 1 of the cp_agent_workbench requirement), "Basisdaten" moved to position 2.
 * The fourth of them,
 * "Verträge / Haftungen", is the pane that lists the Provisionssätze of this Abrechnungskonto and
 * carries all three §7 action dialogs (Betrag überschreiben, Berater wechseln, Satz manuell
 * anlegen) — see {@link CpProvisionAgentContractAgentsComponent}. It replaced the earlier navItem
 * "Verträge" in WP-E14, because the agent, the amount and every liability field moved off
 * `cp.ProvisionContract` onto `cp.ProvisionContractAgent` in WP-E5 (A6-04) and all three endpoints
 * now address a Satz, not a contract. `ObjectBaseModeComponent` subscribes to
 * `?navItem=`/`?sideNavItem=` itself — so NOTHING needs to be rebuilt here (G6.5 only applies to
 * direct `ObjectBaseComponent` subclasses).
 *
 * The third item, "Provisionskonfiguration", is the Beratersicht on the resolved provision
 * configuration ({@link CpProvisionAgentConfigurationComponent}). It sits directly after
 * "Basisdaten" because after "wer ist das" the next question is "was bekommt er", and the item must
 * be reachable without scrolling. `default` stays FALSE — the business rule above allows exactly
 * one default and that remains `kpi`; a second one would make the opened pane depend on iteration
 * order. Nothing else had to be wired: `ObjectBaseModeComponent` matches `action` against
 * `?navItem=` itself, so the route and the entity registry stay untouched.
 *
 * UI text is German with real umlauts; code identifiers are English. The business terms are fixed
 * by 04 §1: "Berater" = am.Agent, "Abrechnungskonto" = cp.ProvisionAgent, "Provisionssatz" =
 * cp.ProvisionContractAgent, "Provisionsart" = cp.ProvisionTypeComponent. Never "Cp", never
 * "Vermittler".
 */
@Component({
    selector: 'cp-provision-agent-base',
    template: '',
    standalone: false
})
export class CpProvisionAgentBaseComponent extends ObjectBaseModeComponent implements OnInit {

    /**
     * um right `cp_agentChange` (U-08) — passed down to the navItem "Verträge / Haftungen" and
     * attached there to all three action buttons.
     *
     * DEFAULTS TO TRUE (optimistic), a documented deviation from 04-frontend.md §5.1 ("Alle
     * Buttons nur mit um-Recht aktiv") taken for all four cp rights alike — orchestrator decision
     * OD-1, reasoning in {@link CpFeatureRight}. In short: this frontend has NO feature-rights
     * service and no cp payload carries a grant, so nothing could ever raise a `false` default —
     * the three action buttons would be permanently inert. That is a lost function, not a
     * closed hole: the security boundary is `cp/cross/um/ProvisionFeatureGuard`, which is itself
     * default-ALLOW and answers a real refusal with HTTP 403. The 403 then demotes this flag in
     * {@link CpProvisionAgentContractAgentsComponent}, which is the honest UI state.
     *
     * Once a feature-rights service exists, ONLY this one spot needs adjusting — `full` and `side`
     * inherit the field.
     */
    canAgentChange: boolean = true;

    /**
     * um right `cp_agentStatement` for "Neu ausstellen" in the navItem "Abrechnungen" — optimistic
     * default, demoted when the reissue endpoint answers 403 (see {@link CpFeatureRight}).
     */
    canAgentStatement: boolean = true;

    /** The statement chosen for "Neu ausstellen" (input of the reissue dialog). */
    reissueStatement: ProvisionAgentStatementDto = null;

    /** Visibility of the reissue dialog. */
    reissueDialogVisible: boolean = false;

    /** The statement list of the navItem "Abrechnungen" (template ref `#statementsList` in full/side). */
    @ViewChild('statementsList') statementsList: CpRefreshableStatementList;

    /**
     * The navigation items of the agent object (04-frontend.md §7 + "Verträge").
     * `action` (not `route`) is the key — it is matched against `?navItem=`.
     *
     * ORDER IS A BUSINESS RULE, not a taste question: "Kennzahlen" is first AND carries
     * `default: true`, "Basisdaten" follows on position 2 with `default: false`. Exactly one item
     * may be the default — two would make the opened section depend on iteration order.
     */
    navigationItems: NavigationItem[] = [
        {
            label: 'Kennzahlen',
            action: 'kpi',
            tooltip: 'Monatsstatistik, Rücklagenkonto, Rücklage gegen Haftung und Deckungsquote',
            icon: 'fa-regular fa-chart-simple',
            toggleable: false,
            default: true
        },
        {
            label: 'Basisdaten',
            action: 'baseData',
            tooltip: 'Stammdaten des Abrechnungskontos',
            icon: 'fa-regular fa-id-card',
            toggleable: false,
            default: false
        },
        {
            label: 'Provisionskonfiguration',
            action: 'configuration',
            tooltip: 'Welcher Satz gilt für welches Geschäft — aufgelöst zum Vertragsbeginn, '
                + 'mit der Matrix, aus der jeder Wert stammt',
            icon: 'fa-regular fa-table-cells',
            toggleable: false,
            default: false
        },
        {
            label: 'Konten & Salden',
            action: 'account',
            tooltip: 'Kontoauszug je Konto mit laufendem Saldo, manuelle Buchung und Umbuchung',
            icon: 'fa-regular fa-scale-balanced',
            toggleable: false,
            default: false
        },
        {
            label: 'Verträge / Haftungen',
            action: 'contractAgents',
            tooltip: 'Provisionssätze dieses Abrechnungskontos mit Haftung; '
                + 'Einstieg in Betrags-Overwrite, Beraterwechsel und manuellen Satz',
            icon: 'fa-regular fa-file-contract',
            toggleable: false,
            default: false
        },
        {
            label: 'Auszahlungen pro Monat',
            action: 'payouts',
            tooltip: 'Summierte Auszahlungen je Monat',
            icon: 'fa-regular fa-money-bill-transfer',
            toggleable: false,
            default: false
        },
        {
            label: 'Abrechnungen',
            action: 'statements',
            tooltip: 'Finale Provisionsabrechnungen dieses Abrechnungskontos mit Download und Neuausstellung',
            icon: 'fa-regular fa-file-invoice',
            toggleable: false,
            default: false
        },
        {
            label: 'Gruppen',
            action: 'groups',
            tooltip: 'Zuordnungshistorie zu Provisionstypen und Gruppen',
            icon: 'fa-regular fa-users',
            toggleable: false,
            default: false
        },
        {
            label: 'Runs/Deckung',
            action: 'reserveCoverage',
            tooltip: 'Deckungsverlauf der Rücklage je Provisionslauf',
            icon: 'fa-regular fa-shield-halved',
            toggleable: false,
            default: false
        }
    ];

    ngOnInit() {
        super.ngOnInit();
        this.initialized = true;
    }

    /**
     * The statement list asked for "Neu ausstellen" — open the reason dialog for that statement.
     * A server-side refusal seen before keeps the dialog closed (the banner explains why).
     */
    handleReissueRequested(statement: ProvisionAgentStatementDto): void {
        if (!statement || !this.canAgentStatement) {
            return;
        }
        this.reissueStatement = statement;
        this.reissueDialogVisible = true;
    }

    /**
     * A reissue succeeded — re-read the list IN PLACE so the new version and the SUPERSEDED one show
     * up. This is the ONE reload trigger of a reissue (the list does not also listen to the
     * object-change event the dialog fires).
     */
    handleStatementReissued(): void {
        this.statementsList?.refresh();
    }

    /** The reissue endpoint answered 403 — demote the right for this view (sticky). */
    handleStatementForbidden(): void {
        this.canAgentStatement = false;
    }

}
